import { useState, useCallback } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { z } from 'zod';
import { Copy, Check as CheckMark, Search, CheckCircle2, AlertTriangle, XCircle, HelpCircle, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { Input } from '@/client/components/ui/input';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Spinner } from '@/client/components/ui/spinner';
import { API_BASE_URL } from '@/client/config';
import type { DiagnoseResponse, AssetDiagnosis, Check, OverallStatus, DeepProbeResult } from '@shared/types';

const masterIDSchema = z.string().regex(
  /^\d{10}_\d+$/,
  'Format: clientID_eventID (e.g., 1234567890_12345)'
);

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'utilities/media-probe',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: MediaProbeComponent,
});

function MediaProbeComponent() {
  const { getAccessTokenSilently } = useAuth0();
  const [masterID, setMasterID] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [result, setResult] = useState<DiagnoseResponse | null>(null);
  const [expandedAssets, setExpandedAssets] = useState<Set<string>>(new Set());
  // Deep probe state
  const [deepProbeResults, setDeepProbeResults] = useState<Map<string, DeepProbeResult>>(new Map());
  const [deepProbeLoading, setDeepProbeLoading] = useState<Set<string>>(new Set());

  const handleDiagnose = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    const validation = masterIDSchema.safeParse(masterID.trim());
    if (!validation.success) {
      setValidationError(validation.error.issues[0].message);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_BASE_URL}/api/events/${encodeURIComponent(masterID)}/diagnose`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${response.status}`);
      }

      const data: DiagnoseResponse = await response.json();
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const toggleAssetExpand = (assetID: string) => {
    setExpandedAssets((prev) => {
      const next = new Set(prev);
      if (next.has(assetID)) {
        next.delete(assetID);
      } else {
        next.add(assetID);
      }
      return next;
    });
  };

  const handleDeepProbe = async (assetID: string, url: string) => {
    if (!url) return;

    setDeepProbeLoading((prev) => new Set(prev).add(assetID));

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/probe/deep`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ uri: url }),
      });

      const data: DeepProbeResult = await response.json();
      setDeepProbeResults((prev) => new Map(prev).set(assetID, data));
    } catch (err) {
      setDeepProbeResults((prev) =>
        new Map(prev).set(assetID, {
          success: false,
          inputUri: url,
          error: err instanceof Error ? err.message : 'Unknown error',
          probeTimeMs: 0,
        })
      );
    } finally {
      setDeepProbeLoading((prev) => {
        const next = new Set(prev);
        next.delete(assetID);
        return next;
      });
    }
  };

  const handleExportText = useCallback(() => {
    if (!result) return;

    const lines: string[] = [];
    const hr = '='.repeat(70);
    const divider = '-'.repeat(70);

    // Header
    lines.push(hr);
    lines.push('EVENT MEDIA PROBE REPORT');
    lines.push(hr);
    lines.push(`Event ID:       ${result.eventID}`);
    lines.push(`Assets:         ${result.assetCount}`);
    lines.push(`Overall Status: ${result.overallStatus.toUpperCase()}`);
    lines.push(`Critical:       ${result.summary.critical}`);
    lines.push(`Warnings:       ${result.summary.warnings}`);
    lines.push(`Generated:      ${new Date().toLocaleString()}`);
    lines.push('');

    // Cross-asset checks
    if (result.crossAssetChecks.length > 0) {
      lines.push(divider);
      lines.push('CROSS-ASSET CHECKS');
      lines.push(divider);
      for (const check of result.crossAssetChecks) {
        const icon = check.status === 'pass' ? 'PASS' : check.status === 'fail' ? 'FAIL' : 'SKIP';
        lines.push(`  [${icon}] ${formatCheckName(check.name)}${check.message ? ` — ${check.message}` : ''}`);
      }
      lines.push('');
    }

    // Assets
    for (const asset of result.assets) {
      lines.push(divider);
      lines.push(`ASSET: ${asset.originalFileName || asset.assetID}`);
      lines.push(divider);
      lines.push(`  Asset ID:   ${asset.assetID}`);
      lines.push(`  Status:     ${asset.status.toUpperCase()}`);
      lines.push(`  URL:        ${asset.url || 'N/A'}`);

      // Probed metadata
      lines.push('');
      lines.push('  Probed Metadata:');
      if (asset.probeResult) {
        const p = asset.probeResult;
        lines.push(`    Duration:     ${p.duration ? `${(p.duration / 60).toFixed(1)} min` : '-'}`);
        lines.push(`    Resolution:   ${p.video ? `${p.video.width}x${p.video.height}` : '-'}`);
        lines.push(`    Video:        ${formatVideoInfo(p.video)}`);
        lines.push(`    Audio:        ${formatAudioInfo(p.audio)}`);
        lines.push(`    Bitrate:      ${formatBitrate(p.bitrate)}`);
        lines.push(`    Pixel Format: ${p.video?.pixelFormat || '-'}`);
        lines.push(`    File Size:    ${formatFileSize(p.size)}`);
      } else {
        lines.push('    (probe failed or no data)');
      }

      // Stored metadata comparison
      const s = asset.storedMetadata;
      if (s.runtimeMin || s.dims || s.avgBitrate || s.fileSize) {
        lines.push('');
        lines.push('  Stored Metadata:');
        if (s.runtimeMin) lines.push(`    Duration:   ${s.runtimeMin} min`);
        if (s.dims) lines.push(`    Resolution: ${s.dims}`);
        if (s.avgBitrate) lines.push(`    Bitrate:    ${s.avgBitrate}`);
        if (s.fileSize) lines.push(`    File Size:  ${formatFileSize(s.fileSize)}`);
      }

      // Checks
      lines.push('');
      lines.push('  Checks:');
      for (const check of asset.checks) {
        const icon = check.status === 'pass' ? 'PASS' : check.status === 'fail' ? 'FAIL' : 'SKIP';
        lines.push(`    [${icon}] ${formatCheckName(check.name)}${check.message ? ` — ${check.message}` : ''}`);
      }

      // Deep probe results if available
      const deepResult = deepProbeResults.get(asset.assetID);
      if (deepResult?.success) {
        lines.push('');
        lines.push('  Deep Probe:');
        if (deepResult.bitrateAnalysis) {
          const b = deepResult.bitrateAnalysis;
          lines.push(`    Bitrate Mode: ${b.mode} (avg ${formatBitrate(b.avgBitrate)})`);
          if (b.nominalBitrate) lines.push(`    Nominal:      ${formatBitrate(b.nominalBitrate)}`);
          if (b.maxBitrate) lines.push(`    Max:          ${formatBitrate(b.maxBitrate)}`);
          if (b.audioBitrateMode) lines.push(`    Audio Mode:   ${b.audioBitrateMode}${b.audioBitrate ? ` (${formatBitrate(b.audioBitrate)})` : ''}`);
        }
        if (deepResult.gopAnalysis) {
          const g = deepResult.gopAnalysis;
          if (g.gopSize) lines.push(`    GOP Size:     ${g.gopSize} frames${g.gopDurationSec ? ` / ${g.gopDurationSec}s` : ''}`);
          if (g.gopStructure) lines.push(`    GOP Structure: ${g.gopStructure}`);
        }
      }

      lines.push('');
    }

    lines.push(hr);
    lines.push('END OF REPORT');
    lines.push(hr);

    const text = lines.join('\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `media-probe_${result.eventID}_${new Date().toISOString().slice(0, 10)}.txt`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [result, deepProbeResults]);

  return (
    <Layout>
      <div className="space-y-6">
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-2xl font-semibold">Event Media Probe</h3>
            {result && (
              <Button variant="outline" size="sm" onClick={handleExportText}>
                <Download className="size-4 mr-2" />
                Export as Text
              </Button>
            )}
          </div>
          <p className="text-muted-foreground mb-6">
            Diagnose video assets for an Event by probing files and comparing against stored metadata.
          </p>

          <form onSubmit={handleDiagnose} className="flex gap-2 max-w-lg mb-6">
            <Input
              type="text"
              placeholder="clientID_eventID"
              value={masterID}
              onChange={(e) => {
                setMasterID(e.target.value);
                if (validationError) setValidationError(null);
              }}
              disabled={loading}
              className="font-mono"
            />
            <Button type="submit" disabled={loading || !masterID.trim()}>
              {loading ? <Spinner className="size-4" /> : <Search className="size-4" />}
              <span className="ml-2">Diagnose</span>
            </Button>
          </form>

          {validationError && (
            <p className="text-sm text-red-600 -mt-4 mb-4">{validationError}</p>
          )}

          {error && (
            <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950 rounded-md mb-6">
              {error}
            </div>
          )}
        </section>

        {result && (
          <>
            {/* Summary Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Diagnosis Summary</CardTitle>
                  <StatusBadge status={result.overallStatus} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Event ID</span>
                    <p className="font-mono">{result.eventID}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Assets</span>
                    <p className="font-semibold">{result.assetCount}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Issues</span>
                    <div className="flex gap-2 mt-1">
                      {result.summary.critical > 0 && (
                        <Badge variant="destructive">{result.summary.critical} critical</Badge>
                      )}
                      {result.summary.warnings > 0 && (
                        <Badge variant="outline" className="border-yellow-500 text-yellow-600">
                          {result.summary.warnings} warnings
                        </Badge>
                      )}
                      {result.summary.critical === 0 && result.summary.warnings === 0 && (
                        <Badge variant="outline" className="border-green-500 text-green-600">
                          All checks passed
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                {/* Key check summary */}
                <CheckSummary assets={result.assets} />
              </CardContent>
            </Card>

            {/* Cross-Asset Checks */}
            {result.crossAssetChecks.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Cross-Asset Checks</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {result.crossAssetChecks.map((check, idx) => (
                      <CheckRow key={idx} check={check} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Assets Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Assets</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>File Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Resolution</TableHead>
                        <TableHead>Checks</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.assets.map((asset) => (
                        <AssetRow
                          key={asset.assetID}
                          asset={asset}
                          expanded={expandedAssets.has(asset.assetID)}
                          onToggle={() => toggleAssetExpand(asset.assetID)}
                          deepProbeResult={deepProbeResults.get(asset.assetID)}
                          deepProbeLoading={deepProbeLoading.has(asset.assetID)}
                          onDeepProbe={() => handleDeepProbe(asset.assetID, asset.url || '')}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}

// ============================================================================
// Helper Components
// ============================================================================

function StatusBadge({ status }: { status: OverallStatus }) {
  switch (status) {
    case 'ok':
      return (
        <Badge variant="outline" className="border-green-500 text-green-600">
          <CheckCircle2 className="size-3 mr-1" /> OK
        </Badge>
      );
    case 'warning':
      return (
        <Badge variant="outline" className="border-yellow-500 text-yellow-600">
          <AlertTriangle className="size-3 mr-1" /> Warning
        </Badge>
      );
    case 'critical':
      return (
        <Badge variant="destructive">
          <XCircle className="size-3 mr-1" /> Critical
        </Badge>
      );
    case 'error':
      return (
        <Badge variant="destructive">
          <XCircle className="size-3 mr-1" /> Error
        </Badge>
      );
  }
}

function CheckIcon({ check }: { check: Check }) {
  if (check.status === 'pass') {
    return <CheckCircle2 className="size-4 text-green-500" />;
  }
  if (check.status === 'fail') {
    if (check.severity === 'critical') {
      return <XCircle className="size-4 text-red-500" />;
    }
    if (check.severity === 'warning') {
      return <AlertTriangle className="size-4 text-yellow-500" />;
    }
    return <AlertTriangle className="size-4 text-blue-500" />;
  }
  return <HelpCircle className="size-4 text-muted-foreground" />;
}

function CheckRow({ check }: { check: Check }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <CheckIcon check={check} />
      <span className="font-medium">{formatCheckName(check.name)}</span>
      {check.message && <span className="text-muted-foreground">- {check.message}</span>}
    </div>
  );
}

function formatCheckName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatFileSize(bytes: number | string | null | undefined): string {
  if (bytes === null || bytes === undefined) return '-';
  const num = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
  if (isNaN(num)) return '-';
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  if (num < 1024 * 1024 * 1024) return `${(num / (1024 * 1024)).toFixed(1)} MB`;
  return `${(num / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatBitrate(bps: number | null | undefined): string {
  if (bps === null || bps === undefined) return '-';
  return `${(bps / 1_000_000).toFixed(2)} Mbps`;
}

function formatVideoInfo(video: { codec: string; width: number; height: number; frameRate: number | null; profile?: string | null; level?: string | null } | null | undefined): string {
  if (!video) return '-';
  const fps = video.frameRate ? ` @ ${video.frameRate.toFixed(2)}fps` : '';
  // Include profile/level if available (e.g., "h264 (High 4.1) @ 29.97fps")
  const profileLevel = video.profile || video.level
    ? ` (${[video.profile, video.level].filter(Boolean).join(' ')})`
    : '';
  return `${video.codec}${profileLevel}${fps}`;
}

function formatAudioInfo(audio: { codec: string; channels: number; sampleRate: number } | null | undefined): string {
  if (!audio) return '-';
  const channelLabel = audio.channels === 1 ? 'mono' : audio.channels === 2 ? 'stereo' : `${audio.channels}ch`;
  const sampleRate = audio.sampleRate >= 1000 ? `${(audio.sampleRate / 1000).toFixed(1)}kHz` : `${audio.sampleRate}Hz`;
  return `${audio.codec} ${channelLabel} (${sampleRate})`;
}

function CheckSummary({ assets }: { assets: AssetDiagnosis[] }) {
  if (assets.length === 0) return null;

  // Aggregate key checks across all assets
  const keyChecks = ['file_exists', 'has_audio', 'web_optimized'] as const;

  const checkStats = keyChecks.map((checkName) => {
    const allChecks = assets.flatMap((a) => a.checks.filter((c) => c.name === checkName));
    const passed = allChecks.filter((c) => c.status === 'pass').length;
    const failed = allChecks.filter((c) => c.status === 'fail').length;
    const total = passed + failed;

    return { name: checkName, passed, failed, total };
  });

  return (
    <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t text-xs">
      {checkStats.map(({ name, passed, failed, total }) => {
        if (total === 0) return null;

        const allPassed = failed === 0;
        const label = formatCheckName(name);

        return (
          <div key={name} className="flex items-center gap-1.5">
            {allPassed ? (
              <CheckCircle2 className="size-3.5 text-green-500" />
            ) : (
              <AlertTriangle className="size-3.5 text-yellow-500" />
            )}
            <span className={allPassed ? 'text-green-600' : 'text-yellow-600'}>
              {allPassed
                ? name === 'file_exists' ? 'All files exist'
                  : name === 'has_audio' ? 'All have audio'
                  : `All ${label.toLowerCase()}`
                : `${passed}/${total} ${label.toLowerCase()}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function CopyableID({ id, truncateLength = 6 }: { id: string; truncateLength?: number }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [id]);

  const truncated = id.length > truncateLength ? `${id.slice(0, truncateLength)}…` : id;

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 font-mono text-xs px-1.5 py-0.5 rounded bg-muted hover:bg-muted/80 transition-colors cursor-pointer"
      title={`Click to copy: ${id}`}
    >
      {truncated}
      {copied ? (
        <CheckMark className="size-3 text-green-500" />
      ) : (
        <Copy className="size-3 text-muted-foreground" />
      )}
    </button>
  );
}

function AssetRow({
  asset,
  expanded,
  onToggle,
  deepProbeResult,
  deepProbeLoading,
  onDeepProbe,
}: {
  asset: AssetDiagnosis;
  expanded: boolean;
  onToggle: () => void;
  deepProbeResult?: DeepProbeResult;
  deepProbeLoading: boolean;
  onDeepProbe: () => void;
}) {
  const passedChecks = asset.checks.filter((c) => c.status === 'pass').length;
  const failedChecks = asset.checks.filter((c) => c.status === 'fail').length;

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onToggle}>
        <TableCell>
          {expanded ? (
            <ChevronUp className="size-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="size-4 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell>
          <div className="max-w-xs truncate" title={asset.originalFileName || asset.assetID}>
            {asset.originalFileName || asset.assetID}
          </div>
        </TableCell>
        <TableCell>
          <StatusBadge status={asset.status} />
        </TableCell>
        <TableCell className="font-mono text-sm">
          {formatDuration(asset.probeResult?.duration)}
        </TableCell>
        <TableCell className="font-mono text-sm">
          {asset.probeResult?.video
            ? `${asset.probeResult.video.width}x${asset.probeResult.video.height}`
            : '-'}
        </TableCell>
        <TableCell>
          <span className="text-green-600">{passedChecks} passed</span>
          {failedChecks > 0 && (
            <span className="text-red-500 ml-2">{failedChecks} failed</span>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/30 p-4">
            <div className="grid md:grid-cols-2 gap-6">
              {/* URL */}
              <div>
                <h4 className="font-semibold mb-2 text-sm">URL</h4>
                <p className="text-xs font-mono break-all text-muted-foreground">
                  {asset.url || 'N/A'}
                </p>
              </div>

              {/* Metadata Comparison */}
              <div>
                <h4 className="font-semibold mb-2 text-sm">Metadata</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div className="text-muted-foreground">Asset ID:</div>
                  <div>
                    <CopyableID id={asset.assetID} />
                  </div>

                  <div className="text-muted-foreground">Video:</div>
                  <div className="font-mono">{formatVideoInfo(asset.probeResult?.video)}</div>

                  <div className="text-muted-foreground">Audio:</div>
                  <div className="font-mono">{formatAudioInfo(asset.probeResult?.audio)}</div>

                  <div className="text-muted-foreground">Resolution:</div>
                  <div className="font-mono">
                    {asset.probeResult?.video
                      ? `${asset.probeResult.video.width}x${asset.probeResult.video.height}`
                      : '-'}
                    {asset.storedMetadata.dims && (
                      <span className="text-muted-foreground"> (stored: {asset.storedMetadata.dims})</span>
                    )}
                  </div>

                  <div className="text-muted-foreground">Duration:</div>
                  <div className="font-mono">
                    {asset.probeResult?.duration
                      ? `${(asset.probeResult.duration / 60).toFixed(1)} min`
                      : '-'}
                    {asset.storedMetadata.runtimeMin && (
                      <span className="text-muted-foreground"> (stored: {asset.storedMetadata.runtimeMin} min)</span>
                    )}
                  </div>

                  <div className="text-muted-foreground">Bitrate:</div>
                  <div className="font-mono">{formatBitrate(asset.probeResult?.bitrate)}</div>

                  <div className="text-muted-foreground">Pixel Format:</div>
                  <div className="font-mono">{asset.probeResult?.video?.pixelFormat || '-'}</div>

                  <div className="text-muted-foreground">File Size:</div>
                  <div className="font-mono">
                    {formatFileSize(asset.probeResult?.size)}
                    {asset.storedMetadata.fileSize && (
                      <span className="text-muted-foreground"> (stored: {formatFileSize(asset.storedMetadata.fileSize)})</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Checks */}
              <div className="md:col-span-2">
                <h4 className="font-semibold mb-2 text-sm">Checks</h4>
                <div className="grid md:grid-cols-2 gap-2">
                  {asset.checks.map((check, idx) => (
                    <CheckRow key={idx} check={check} />
                  ))}
                </div>
              </div>

              {/* Deep Probe */}
              <div className="md:col-span-2 pt-4 border-t">
                <div className="flex items-center gap-3 mb-3">
                  <h4 className="font-semibold text-sm">Deep Probe</h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeepProbe();
                    }}
                    disabled={deepProbeLoading || !asset.url}
                    className="h-7 text-xs"
                  >
                    {deepProbeLoading ? (
                      <>
                        <Spinner className="size-3 mr-1" />
                        Analyzing...
                      </>
                    ) : deepProbeResult ? (
                      'Re-analyze'
                    ) : (
                      'Analyze Bitrate & GOP'
                    )}
                  </Button>
                  {deepProbeResult?.probeTimeMs && deepProbeResult.probeTimeMs > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ({(deepProbeResult.probeTimeMs / 1000).toFixed(1)}s)
                    </span>
                  )}
                </div>

                {deepProbeResult && (
                  <DeepProbeDisplay result={deepProbeResult} />
                )}

                {!deepProbeResult && !deepProbeLoading && (
                  <p className="text-xs text-muted-foreground">
                    Analyzes bitrate mode (CBR/VBR) and GOP structure using MediaInfo.
                  </p>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function BitrateModeBadge({ mode }: { mode: string }) {
  const className = mode === 'CBR'
    ? 'border-green-500 text-green-600'
    : mode === 'VBR'
      ? 'border-blue-500 text-blue-600'
      : mode === 'ABR'
        ? 'border-yellow-500 text-yellow-600'
        : '';
  return (
    <Badge variant="outline" className={className}>
      {mode}
    </Badge>
  );
}

/** 3-column row: Simple Name | Value | Technical Context */
function ProbeRow({
  label,
  value,
  context,
}: {
  label: string;
  value: React.ReactNode;
  context?: string;
}) {
  return (
    <div className="grid grid-cols-[120px_1fr_1fr] gap-x-4 items-center text-sm">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-mono">{value}</div>
      <div className="text-muted-foreground text-xs">{context}</div>
    </div>
  );
}

function DeepProbeDisplay({ result }: { result: DeepProbeResult }) {
  if (!result.success) {
    return (
      <div className="text-sm text-red-500 flex items-center gap-2">
        <XCircle className="size-4" />
        {result.error || 'Deep probe failed'}
      </div>
    );
  }

  const { bitrateAnalysis, gopAnalysis } = result;

  // Build technical context strings
  const bitrateContext = bitrateAnalysis
    ? [
        bitrateAnalysis.nominalBitrate && `target ${formatBitrate(bitrateAnalysis.nominalBitrate)}`,
        bitrateAnalysis.maxBitrate && `max ${formatBitrate(bitrateAnalysis.maxBitrate)}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  const gopContext = gopAnalysis?.gopStructure || '';

  const keyframeValue = gopAnalysis
    ? `${gopAnalysis.gopSize ?? '?'} frames${gopAnalysis.gopDurationSec ? ` / ${gopAnalysis.gopDurationSec}s` : ''}`
    : null;

  return (
    <div className="space-y-4 text-xs">
      {/* Video Section */}
      {bitrateAnalysis && (
        <div className="space-y-1.5">
          <h5 className="font-medium text-sm">Video</h5>
          <ProbeRow
            label="Bitrate Mode"
            value={
              <span className="flex items-center gap-2">
                <BitrateModeBadge mode={bitrateAnalysis.mode} />
                {formatBitrate(bitrateAnalysis.avgBitrate)}
              </span>
            }
            context={bitrateContext}
          />
          {gopAnalysis && keyframeValue && (
            <ProbeRow
              label="Keyframes"
              value={keyframeValue}
              context={`GOP ${gopContext}`}
            />
          )}
        </div>
      )}

      {/* Audio Section */}
      {bitrateAnalysis?.audioBitrateMode && (
        <div className="space-y-1.5">
          <h5 className="font-medium text-sm">Audio</h5>
          <ProbeRow
            label="Bitrate Mode"
            value={
              <span className="flex items-center gap-2">
                <BitrateModeBadge mode={bitrateAnalysis.audioBitrateMode} />
                {bitrateAnalysis.audioBitrate ? formatBitrate(bitrateAnalysis.audioBitrate) : ''}
              </span>
            }
            context={bitrateAnalysis.audioBitrateMode === 'CBR' ? 'constant bitrate' : 'variable bitrate'}
          />
        </div>
      )}

      {/* No data message */}
      {!bitrateAnalysis && !gopAnalysis && (
        <div className="text-muted-foreground">
          No analysis data available. MediaInfo could not extract bitrate or GOP information.
        </div>
      )}
    </div>
  );
}
